import * as pc from 'playcanvas';

const SCREEN_HEIGHT = 2.15;
const NEAR = 0.06;
const FAR = 80;

export class SpatialRenderer {
  constructor(canvas, onProgress = () => {}) {
    this.canvas = canvas;
    this.onProgress = onProgress;
    this.eye = { x: 0, y: 0, z: 3.2 };
    this.currentEntity = null;
    this.currentAsset = null;
    this.currentObjectUrl = null;
    this.assetLoaded = false;
    this.fps = 0;
    this._fpsTime = performance.now();
    this._fpsFrames = 0;

    this.app = new pc.Application(canvas, {
      graphicsDeviceOptions: {
        antialias: false,
        alpha: false,
        powerPreference: 'high-performance',
      },
    });

    this.app.graphicsDevice.maxPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    this.app.setCanvasResolution(pc.RESOLUTION_AUTO);
    this.app.scene.ambientLight = new pc.Color(0.08, 0.08, 0.085);
    if (this.app.scene.gsplat) {
      this.app.scene.gsplat.alphaClip = 0.32;
      this.app.scene.gsplat.alphaClipForward = 1 / 255;
    }

    this.camera = new pc.Entity('head-coupled-camera');
    this.camera.addComponent('camera', {
      clearColor: new pc.Color(0.039, 0.039, 0.043, 1),
      nearClip: NEAR,
      farClip: FAR,
      toneMapping: pc.TONEMAP_ACES,
      frustumCulling: false,
    });
    this.app.root.addChild(this.camera);
    this.camera.setEulerAngles(0, 0, 0);

    this.camera.camera.calculateProjection = (matrix) => {
      const aspect = Math.max(0.35, this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight));
      const screenWidth = SCREEN_HEIGHT * aspect;
      const distance = Math.max(this.eye.z, NEAR * 2);
      const scale = NEAR / distance;
      const left = (-screenWidth * 0.5 - this.eye.x) * scale;
      const right = (screenWidth * 0.5 - this.eye.x) * scale;
      const bottom = (-SCREEN_HEIGHT * 0.5 - this.eye.y) * scale;
      const top = (SCREEN_HEIGHT * 0.5 - this.eye.y) * scale;
      matrix.setFrustum(left, right, bottom, top, NEAR, FAR);
    };

    this.app.on('update', () => {
      this.camera.setPosition(this.eye.x, this.eye.y, this.eye.z);
      this._fpsFrames += 1;
      const now = performance.now();
      if (now - this._fpsTime >= 500) {
        this.fps = Math.round((this._fpsFrames * 1000) / (now - this._fpsTime));
        this._fpsFrames = 0;
        this._fpsTime = now;
      }
    });

    this._resize = () => this.app.resizeCanvas();
    window.addEventListener('resize', this._resize, { passive: true });
    this.app.start();
  }

  setEye(eye) {
    this.eye.x = eye.x;
    this.eye.y = eye.y;
    this.eye.z = eye.z;
  }

  async loadDefault() {
    return this.loadAsset('/assets/biker.compressed.ply', 'biker.compressed.ply', true);
  }

  async loadFile(file) {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['ply', 'sog'].includes(extension)) {
      throw new Error('Bitte eine .ply- oder .sog-Datei wählen.');
    }
    if (this.currentObjectUrl) URL.revokeObjectURL(this.currentObjectUrl);
    this.currentObjectUrl = URL.createObjectURL(file);
    return this.loadAsset(this.currentObjectUrl, file.name, false, file.size);
  }

  loadAsset(url, filename, isDefault = false, size = undefined) {
    this.assetLoaded = false;
    this.onProgress(0, `Lade ${isDefault ? '3D-Szene' : filename}`);

    const asset = new pc.Asset(filename, 'gsplat', { url, filename, size });
    this.app.assets.add(asset);

    return new Promise((resolve, reject) => {
      asset.on('progress', (received, total) => {
        const ratio = total > 0 ? Math.min(received / total, 0.93) : 0.28;
        this.onProgress(ratio, `Lade ${isDefault ? '3D-Szene' : filename}`);
      });

      asset.once('error', (error) => {
        this.app.assets.remove(asset);
        asset.unload();
        reject(new Error(typeof error === 'string' ? error : 'Gaussian Splat konnte nicht geladen werden.'));
      });

      asset.once('load', () => {
        const previousEntity = this.currentEntity;
        const previousAsset = this.currentAsset;

        const entity = new pc.Entity(isDefault ? 'biker-splat' : filename);
        entity.addComponent('gsplat', { asset });

        if (isDefault) {
          entity.setLocalPosition(0, -0.9, -2.25);
          entity.setLocalEulerAngles(170, 122, 0);
          entity.setLocalScale(0.78, 0.78, 0.78);
        } else {
          entity.setLocalPosition(0, -0.35, -2.4);
          entity.setLocalEulerAngles(extensionRotation(filename), 0, 0);
          entity.setLocalScale(0.9, 0.9, 0.9);
        }

        this.app.root.addChild(entity);
        this.currentEntity = entity;
        this.currentAsset = asset;
        this.assetLoaded = true;
        this.onProgress(1, isDefault ? 'Bereit' : `${filename} geladen`);

        requestAnimationFrame(() => {
          previousEntity?.destroy();
          if (previousAsset) {
            this.app.assets.remove(previousAsset);
            previousAsset.unload();
          }
        });
        resolve(entity);
      });

      this.app.assets.load(asset);
    });
  }

  destroy() {
    window.removeEventListener('resize', this._resize);
    if (this.currentObjectUrl) URL.revokeObjectURL(this.currentObjectUrl);
    this.app.destroy();
  }
}

function extensionRotation(filename) {
  return filename.toLowerCase().endsWith('.ply') ? 180 : 0;
}
